
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Newspaper, 
  FileText, 
  UserCheck, 
  Users, 
  ChartBar,
  Database
} from 'lucide-react';
import RiskColorChart from '@/components/company-profile/RiskColorChart';

interface DataSourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'left' | 'right';
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({ icon, title, description, position }) => {
  // Map positions to appropriate Tailwind classes for layout
  const positionClasses = {
    'top-left': 'md:col-start-1 md:col-end-3 md:row-start-1',
    'top-right': 'md:col-start-5 md:col-end-7 md:row-start-1',
    'left': 'md:col-start-1 md:col-end-3 md:row-start-2',
    'right': 'md:col-start-5 md:col-end-7 md:row-start-2',
    'bottom-left': 'md:col-start-1 md:col-end-3 md:row-start-3',
    'bottom-right': 'md:col-start-5 md:col-end-7 md:row-start-3',
  };

  return (
    <Card className={`card-hover border border-redflags-light/70 ${positionClasses[position]}`}>
      <CardContent className="p-5 flex items-start gap-3">
        <div className="bg-redflags-red/10 p-2 rounded-full flex-shrink-0">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-redflags-dark mb-1">{title}</h3>
          <p className="text-redflags-gray text-sm">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const HowItWorks = () => {
  return (
    <section className="py-20 px-6 bg-gradient-to-b from-white to-redflags-light/20">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl font-bold mb-4 text-redflags-dark">How It Works</h2>
          <p className="text-redflags-gray max-w-2xl mx-auto">
            Our redundancy risk assessment is based on 6 key data sources that together provide a comprehensive view of company stability.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-6 md:grid-rows-3 gap-4 relative">
          {/* Central risk assessment display */}
          <div className="md:col-start-3 md:col-end-5 md:row-start-1 md:row-end-4 flex flex-col items-center justify-center bg-white p-5 rounded-xl shadow-sm border border-redflags-light/70 z-10 mb-8 md:mb-0">
            <h3 className="text-xl font-semibold text-redflags-dark mb-4 text-center">Redundancy Risk Rating</h3>
            
            <div className="bg-[#E5DEFF] rounded-lg p-4 flex flex-col items-center mb-4">
              <div className="p-3 rounded-full bg-[#E5DEFF] border border-indigo-200 mb-2">
                <UserCheck className="w-6 h-6 text-indigo-500" />
              </div>
              <h4 className="text-lg font-semibold text-indigo-700">Low Risk</h4>
            </div>
            
            <RiskColorChart riskLevel="Low Risk" />
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              RedFlags AI powered algorithm assessing 6 key data sources
            </p>
          </div>
          
          {/* Data source cards positioned around the central element */}
          <DataSourceCard 
            icon={<Newspaper className="w-5 h-5 text-redflags-red" />}
            title="Media Monitoring"
            description="Analysis of news and media mentions to identify red flags"
            position="top-left"
          />
          
          <DataSourceCard 
            icon={<FileText className="w-5 h-5 text-redflags-red" />}
            title="Financial Reporting"
            description="Company financial health indicators and trends"
            position="top-right"
          />
          
          <DataSourceCard 
            icon={<UserCheck className="w-5 h-5 text-redflags-red" />}
            title="Recruiter Reviews"
            description="Insights from industry recruiters about hiring patterns"
            position="left"
          />
          
          <DataSourceCard 
            icon={<Users className="w-5 h-5 text-redflags-red" />}
            title="Employee Reviews"
            description="Insights from current and former employees"
            position="right"
          />
          
          <DataSourceCard 
            icon={<ChartBar className="w-5 h-5 text-redflags-red" />}
            title="Redundancy Statistics"
            description="Historical and predicted workforce changes"
            position="bottom-left"
          />
          
          <DataSourceCard 
            icon={<Database className="w-5 h-5 text-redflags-red" />}
            title="Investment Tracking"
            description="Funding rounds and investment activity"
            position="bottom-right"
          />
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
